export type FrequencyLeftOutReason = 'oneOff' | 'variable' | 'noFrequency';

// Rates match the backend's in cashflows/domain/frequency.py, so the tab and the cashflow model agree
const FREQUENCIES = {
  Daily: { perYear: 365 },
  Weekly: { perYear: 52 },
  Fortnightly: { perYear: 26 },
  Monthly: { perYear: 12 },
  Quarterly: { perYear: 4 },
  Annually: { perYear: 1 },
  'One-off': { leftOut: 'oneOff' },
  Variable: { leftOut: 'variable' },
} as const satisfies Record<
  string,
  { perYear: number } | { leftOut: Exclude<FrequencyLeftOutReason, 'noFrequency'> }
>;

export type CashflowFrequency = keyof typeof FREQUENCIES;

export const cashflowFrequencyOptions = (Object.keys(FREQUENCIES) as CashflowFrequency[]).map(
  frequency => ({ value: frequency, label: frequency }),
);

// A frequency the backend sends that this table doesn't know comes back undefined, so the item is
// left out of the totals and listed rather than silently dropped
function frequencyRule(frequency: CashflowFrequency | null | undefined) {
  return frequency ? FREQUENCIES[frequency] : undefined;
}

export function leftOutReason(
  frequency: CashflowFrequency | null | undefined,
): FrequencyLeftOutReason | null {
  const rule = frequencyRule(frequency);
  if (!rule) return 'noFrequency';
  return 'leftOut' in rule ? rule.leftOut : null;
}

export function calculateAnnualAmount(
  amount: number | null | undefined,
  frequency: CashflowFrequency | null | undefined,
): number | null {
  const rule = frequencyRule(frequency);
  if (amount == null || !rule || !('perYear' in rule)) return null;
  return amount * rule.perYear;
}

export function calculateMonthlyAmount(
  amount: number | null | undefined,
  frequency: CashflowFrequency | null | undefined,
): number | null {
  const annual = calculateAnnualAmount(amount, frequency);
  return annual === null ? null : annual / 12;
}

export type Period = 'monthly' | 'annually';

export function amountForPeriod(
  amount: number | null | undefined,
  frequency: CashflowFrequency | null | undefined,
  period: Period,
): number | null {
  return period === 'monthly'
    ? calculateMonthlyAmount(amount, frequency)
    : calculateAnnualAmount(amount, frequency);
}

export function totalForPeriod<T extends { frequency?: CashflowFrequency | null }>(
  items: T[] | undefined,
  amountOf: (item: T) => number | null | undefined,
  period: Period,
): number {
  return (items ?? []).reduce(
    (total, item) => total + (amountForPeriod(amountOf(item), item.frequency, period) ?? 0),
    0,
  );
}
